import { createContext } from 'react';

import type { LoginInput, PortalUser, SignInResult, VerifyOtpInput } from '@/types/auth';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthContextValue {
  status: AuthStatus;
  user: PortalUser | null;
  /**
   * Step 1 of two. Resolves with the outcome — and `signed-in` is the one it
   * almost never returns, since email OTP is mandatory: a correct password
   * yields `otp-required` and a challenge to finish with `completeOtp`.
   *
   * Neither non-terminal outcome is thrown, because neither is a failure the
   * user can fix by retyping: `account-type-required` is a question, and
   * `otp-required` is the next step. Genuine failures (bad credentials,
   * validation, network) still reject with an AppError.
   */
  signIn: (input: LoginInput) => Promise<SignInResult>;
  /**
   * Step 2: exchanges the challenge and the emailed code for a token, and is
   * the only thing here that actually signs a user in.
   *
   * Rejects with an AppError when the code is wrong. Callers should check
   * `isOtpChallengeExpired` on it — that distinguishes "try the code again"
   * from "the challenge is dead, start over at sign-in".
   */
  completeOtp: (input: VerifyOtpInput) => Promise<void>;
  /** Emails a fresh code for the same challenge; resolves with the server's message. */
  resendOtp: (challenge: string) => Promise<string>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
